import React, { useState, useEffect, useMemo } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Typography
} from '@mui/material'
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material'
import { SelectChangeEvent } from '@mui/material/Select'
import { pmoAssigneeService, PMOAssignee } from '../services/pmoAssigneeService'
import { useJiraConfig } from '../hooks/useJiraConfig'

interface PMOAssigneeFilterProps {
  selectedAssignees: string[]
  onAssigneeChange: (assignees: string[]) => void
  disabled?: boolean
  showStats?: boolean
  variant?: 'standard' | 'outlined' | 'filled'
  size?: 'small' | 'medium'
  fullWidth?: boolean
  label?: string
  placeholder?: string
  maxWidth?: number | string
}

const PMOAssigneeFilter: React.FC<PMOAssigneeFilterProps> = ({
  selectedAssignees = [],
  onAssigneeChange,
  disabled = false,
  showStats = false,
  variant = 'outlined',
  size = 'small',
  fullWidth = true,
  label = 'PMO Assignee',
  placeholder = 'Search PMO assignees...',
  maxWidth = 300
}) => {
  const [assignees, setAssignees] = useState<PMOAssignee[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [open, setOpen] = useState(false)

  // Check Jira configuration
  const { data: jiraConfig, isLoading: configLoading } = useJiraConfig();

  // Debounce search term to avoid immediate filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300) // 300ms delay

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Load PMO assignees only when Jira is configured
  useEffect(() => {
    if (!configLoading && jiraConfig?.isConfigured) {
      loadPMOAssignees()
    } else if (!configLoading && jiraConfig && !jiraConfig.isConfigured) {
      setError('Jira configuration required. Please configure Jira settings to load PMO assignees.')
      setAssignees([])
    }
  }, [configLoading, jiraConfig?.isConfigured])

  const loadPMOAssignees = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const result = await pmoAssigneeService.getAllPMOAssignees()
      
      if (result.success) {
        setAssignees(result.data)
      } else {
        setError(result.message || 'Failed to load PMO assignees')
      }
    } catch (err) {
      setError('Failed to load PMO assignees')
      console.error('Error loading PMO assignees:', err)
    } finally {
      setLoading(false)
    }
  }

  // Filter assignees based on debounced search term
  const filteredAssignees = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return assignees
    }
    return pmoAssigneeService.filterPMOAssignees(assignees, debouncedSearchTerm)
  }, [assignees, debouncedSearchTerm])

  // Get selected assignee objects
  const selectedAssigneeObjects = useMemo(() => {
    return selectedAssignees.map(assigneeName => 
      assignees.find(a => a.name === assigneeName)
    ).filter(Boolean) as PMOAssignee[]
  }, [selectedAssignees, assignees])

  const handleAssigneeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[]
    onAssigneeChange(value)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const handleClearSearch = () => {
    setSearchTerm('')
  }

  const handleClearAll = () => {
    console.log('PMO Assignee Clear All clicked')
    onAssigneeChange([])
    setOpen(false) // Close the dropdown after clearing
  }

  const handleOpen = () => {
    setOpen(true)
    setSearchTerm('') // Clear search when reopening
    // Auto-focus on search input when dropdown opens
    setTimeout(() => {
      const searchInput = document.querySelector('#pmo-assignee-search-input') as HTMLInputElement
      if (searchInput) {
        searchInput.focus()
      }
    }, 100)
  }

  const handleClose = () => {
    setOpen(false)
    setSearchTerm('') // Clear search when closing
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ maxWidth }}
        action={
          <IconButton size="small" onClick={loadPMOAssignees}>
            <SearchIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    )
  }

  return (
    <Box sx={{
      minWidth: 180, // Reduced from 200 to 180
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      position: 'relative'
    }}> {/* Dynamic width based on content */}
      <FormControl
        fullWidth
        size="small"
        disabled={disabled || loading}
        sx={{
          '& .MuiInputBase-root': {
            alignItems: 'baseline'
          }
        }}
      >
        <InputLabel id="pmo-assignee-filter-label" sx={{ fontSize: '0.8rem' }}>{label}</InputLabel>
        
        <Select
          labelId="pmo-assignee-filter-label"
          multiple
          value={selectedAssignees}
          onChange={handleAssigneeChange}
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}> {/* Reduced gap from 0.5 to 0.25 */}
              {(selected as string[]).map((assigneeName) => {
                const assignee = assignees.find(a => a.name === assigneeName)
                return (
                  <Chip
                    key={assigneeName}
                    label={assignee?.displayName || assigneeName}
                    size="small"
                    onDelete={(event) => {
                      event.stopPropagation()
                      const newSelected = selectedAssignees.filter(a => a !== assigneeName)
                      onAssigneeChange(newSelected)
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    deleteIcon={<ClearIcon />}
                    sx={{
                      height: 20, // Slightly smaller chip height
                      fontSize: '0.75rem' // Smaller font size
                    }}
                  />
                )
              })}
            </Box>
          )}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon fontSize="small" />
              {label}
            </Box>
          }
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 400,
                minWidth: 'auto', // Dynamic width based on content
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          {/* Search input at the top of the dropdown */}
          <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              id="pmo-assignee-search-input"
              fullWidth
              size="small"
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleSearchChange}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                // Allow arrow keys to navigate through menu items
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  return // Don't stop propagation for arrow keys
                }
                e.stopPropagation()
              }}
            />
          </Box>

          {/* Clear all button */}
          {selectedAssignees.length > 0 && (
            <MenuItem
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleClearAll()
              }}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                mb: 1,
                color: 'primary.main'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontStyle: 'italic' }}>
                <ClearIcon fontSize="small" />
                Clear all selections
              </Box>
            </MenuItem>
          )}

          {/* Loading state */}
          {loading && (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Loading PMO assignees...
              </Box>
            </MenuItem>
          )}

          {/* No results */}
          {!loading && filteredAssignees.length === 0 && debouncedSearchTerm && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                No PMO assignees found for "{debouncedSearchTerm}"
              </Typography>
            </MenuItem>
          )}

          {/* PMO Assignee options */}
          {!loading && filteredAssignees.map((assignee) => (
            <MenuItem key={assignee.id} value={assignee.name} sx={{ py: 0.75 }}> {/* Reduced padding */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}> {/* Reduced gap from 1 to 0.75 */}
                <PersonIcon fontSize="small" color="primary" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.875rem' }}> {/* Slightly smaller font */}
                    {assignee.displayName}
                  </Typography>
                  {assignee.email && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {assignee.email}
                    </Typography>
                  )}
                </Box>
                {showStats && (
                  <Tooltip title="View analytics">
                    <IconButton size="small">
                      <AnalyticsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </MenuItem>
          ))}

          {/* Empty state */}
          {!loading && assignees.length === 0 && !searchTerm && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                No PMO assignees available
              </Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  )
}

export default PMOAssigneeFilter
